/**
 * Shared incentive types for API contracts between frontend and backend
 * Covers employee commissions and bonuses
 */
import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
// ===== ENUM ZOD SCHEMAS =====
export const CommissionTypeSchema = z.enum(["FLAT_RATE", "TIERED_RATE", "MULTIPLIER"]);
export const CommissionBaseSchema = z.enum(["SALES", "COGS"]);
export const TierRateTypeSchema = z.enum(["MARGINAL", "BLENDED"]);
export const IncentivePaymentScheduleSchema = z.enum([
    "WEEKLY",
    "EVERY_TWO_WEEKS",
    "TWICE_A_MONTH",
    "MONTHLY",
    "QUARTERLY",
    "ANNUAL",
]);
export const BonusTypeSchema = z.enum(["RETENTION", "PERFORMANCE"]);
export const BonusConditionSchema = z.enum([
    "ANNIVERSARY_DATE",
    "ANNUAL_BONUS_TIME",
    "AFTER_1_YEAR",
    "AFTER_2_YEARS",
]);
export const GoalTypeSchema = z.enum(["INDIVIDUAL_KPI", "TEAM_GOAL", "COMPANY_GOAL"]);
export const GoalMetricSchema = z.enum([
    "PUNCTUALITY",
    "TOTAL_HOURS",
    "SALES",
    "TIPS_PERCENT_OF_SALES",
    "REVIEWS",
    "TURNOVER",
]);
export const PayoutTypeSchema = z.enum(["FIXED_AMOUNT", "PERCENTAGE_OF_SALARY"]);
export const GoalPeriodSchema = z.enum(["MONTHLY", "QUARTERLY", "TWICE_A_YEAR", "ANNUALLY"]);
// ===== COMMISSION TIER SCHEMA =====
export const CommissionTierSchema = z.object({
    threshold: z.number().nonnegative(),
    rate: z.number().nonnegative().max(100),
});
// ===== COMMISSION INPUT SCHEMAS (Discriminated Union) =====
const BaseCommissionSchema = z.object({
    employmentId: uuidSchema,
    commissionBase: CommissionBaseSchema,
    paymentSchedule: IncentivePaymentScheduleSchema,
    notes: z.string().optional(),
});
export const FlatRateCommissionSchema = BaseCommissionSchema.extend({
    commissionType: z.literal("FLAT_RATE"),
    rate: z.number().nonnegative().max(100), // percentage
    threshold: z.number().nonnegative().optional(), // minimum to qualify
});
export const TieredRateCommissionSchema = BaseCommissionSchema.extend({
    commissionType: z.literal("TIERED_RATE"),
    tierRateType: TierRateTypeSchema,
    tiers: z.array(CommissionTierSchema).min(1),
});
export const MultiplierCommissionSchema = BaseCommissionSchema.extend({
    commissionType: z.literal("MULTIPLIER"),
    baseRate: z.number().nonnegative().max(100),
    quoteThreshold: z.number().nonnegative(),
    multiplierValue: z.number().positive(),
    multiplierPeriod: z.string(),
});
export const CreateEmployeeCommissionInputSchema = z.discriminatedUnion("commissionType", [
    FlatRateCommissionSchema,
    TieredRateCommissionSchema,
    MultiplierCommissionSchema,
]);
// ===== UPDATE COMMISSION SCHEMA =====
export const UpdateEmployeeCommissionInputSchema = z.object({
    commissionBase: CommissionBaseSchema.optional(),
    paymentSchedule: IncentivePaymentScheduleSchema.optional(),
    rate: z.number().nonnegative().max(100).optional(),
    threshold: z.number().nonnegative().optional(),
    tierRateType: TierRateTypeSchema.optional(),
    tiers: z.array(CommissionTierSchema).min(1).optional(),
    baseRate: z.number().nonnegative().max(100).optional(),
    quoteThreshold: z.number().nonnegative().optional(),
    multiplierValue: z.number().positive().optional(),
    multiplierPeriod: z.string().optional(),
    notes: z.string().optional(),
});
// ===== BONUS INPUT SCHEMAS (Discriminated Union) =====
const BaseBonusSchema = z.object({
    employmentId: uuidSchema,
    notes: z.string().optional(),
});
export const RetentionBonusSchema = BaseBonusSchema.extend({
    bonusType: z.literal("RETENTION"),
    paymentFrequency: IncentivePaymentScheduleSchema,
    paymentAmount: z.number().positive(),
    bonusCondition: BonusConditionSchema,
});
export const PerformanceBonusSchema = BaseBonusSchema.extend({
    bonusType: z.literal("PERFORMANCE"),
    goalType: GoalTypeSchema,
    goalMetric: GoalMetricSchema,
    targetValue: z.number().positive(),
    payoutType: PayoutTypeSchema,
    payoutAmount: z.number().positive(),
    goalPeriod: GoalPeriodSchema,
});
export const CreateEmployeeBonusInputSchema = z.discriminatedUnion("bonusType", [
    RetentionBonusSchema,
    PerformanceBonusSchema,
]);
// ===== UPDATE BONUS SCHEMA =====
export const UpdateEmployeeBonusInputSchema = z.object({
    paymentFrequency: IncentivePaymentScheduleSchema.optional(),
    paymentAmount: z.number().positive().optional(),
    bonusCondition: BonusConditionSchema.optional(),
    goalType: GoalTypeSchema.optional(),
    goalMetric: GoalMetricSchema.optional(),
    targetValue: z.number().positive().optional(),
    payoutType: PayoutTypeSchema.optional(),
    payoutAmount: z.number().positive().optional(),
    goalPeriod: GoalPeriodSchema.optional(),
    notes: z.string().optional(),
});
// ===== RESPONSE SCHEMAS FOR ORPC =====
export const EmployeeCommissionResponseSchema = z.object({
    id: z.uuid(),
    commissionType: CommissionTypeSchema,
    commissionBase: CommissionBaseSchema,
    paymentSchedule: IncentivePaymentScheduleSchema,
    rate: z.number().nullable(),
    threshold: z.number().nullable(),
    tierRateType: TierRateTypeSchema.nullable(),
    tiers: z.array(CommissionTierSchema).nullable(),
    baseRate: z.number().nullable(),
    quoteThreshold: z.number().nullable(),
    multiplierValue: z.number().nullable(),
    multiplierPeriod: z.string().nullable(),
    isActive: z.boolean(),
    notes: z.string().nullable(),
    employmentId: z.uuid(),
    createdAt: z.string(), // Already ISO string from service
    updatedAt: z.string(), // Already ISO string from service
});
export const EmployeeBonusResponseSchema = z.object({
    id: z.uuid(),
    bonusType: BonusTypeSchema,
    paymentFrequency: IncentivePaymentScheduleSchema.nullable(),
    paymentAmount: z.number().nullable(),
    bonusCondition: BonusConditionSchema.nullable(),
    goalType: GoalTypeSchema.nullable(),
    goalMetric: GoalMetricSchema.nullable(),
    targetValue: z.number().nullable(),
    payoutType: PayoutTypeSchema.nullable(),
    payoutAmount: z.number().nullable(),
    goalPeriod: GoalPeriodSchema.nullable(),
    isActive: z.boolean(),
    notes: z.string().nullable(),
    employmentId: z.uuid(),
    createdAt: z.string(), // Already ISO string from service
    updatedAt: z.string(), // Already ISO string from service
});
export const IncentivesResponseSchema = z.object({
    commissions: z.array(EmployeeCommissionResponseSchema),
    bonuses: z.array(EmployeeBonusResponseSchema),
});
